import { Component, inject, signal } from '@angular/core';
import { take } from 'rxjs';
import { UwIcon } from '../../shared/ui/icon/uw-icon';
import { UwTabs, UwTabItem } from '../../shared/ui/tabs/uw-tabs';
import { UwAiBriefing } from '../../shared/domain/ai-briefing/uw-ai-briefing';
import { UwIntentQueue } from '../../shared/domain/intent-queue/uw-intent-queue';
import { UwAiActionHub } from '../../shared/domain/ai-action-hub/uw-ai-action-hub';
import { CopilotService } from '../../core/services/copilot.service';
import { CopilotStore, CopilotPanel } from '../../core/stores/copilot.store';
import type { AiBriefing } from '../../core/models/ai.model';

const PANELS: UwTabItem[] = [
  { id: 'briefing', label: 'Briefing', icon: 'sparkles' },
  { id: 'queue', label: 'Queue', icon: 'bot' },
  { id: 'actions', label: 'Actions', icon: 'zap' },
];

@Component({
  selector: 'uw-copilot-triage-page',
  standalone: true,
  imports: [UwIcon, UwTabs, UwAiBriefing, UwIntentQueue, UwAiActionHub],
  template: `
    <div class="flex h-full flex-col">
      <div class="px-4 pt-4">
        <div class="mb-3 flex items-center gap-2 rounded-lg border border-ai/25 bg-ai-soft/40 px-3 py-2">
          <uw-icon name="bot" [size]="14" class="shrink-0 text-ai" />
          <input
            #nl
            class="w-full bg-transparent text-[12.5px] text-primary placeholder:text-text-muted focus:outline-none"
            placeholder="Ask Copilot anything — try “show high priority”, “run a risk analysis”…"
            [value]="query()"
            (input)="query.set(nl.value)"
            (keydown.enter)="ask()"
            aria-label="Ask Copilot"
          />
          <button
            class="rounded-md bg-ai px-2 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-ai-hover"
            [disabled]="asking()"
            (click)="ask()"
          >
            {{ asking() ? '…' : 'Ask' }}
          </button>
        </div>

        @if (reply(); as r) {
          <div class="mb-3 flex items-start gap-2 rounded-lg border border-line bg-surface-elevated px-3 py-2.5">
            <uw-icon name="sparkles" [size]="14" class="mt-0.5 shrink-0 text-ai" />
            <p class="text-[12px] leading-relaxed text-secondary">{{ r }}</p>
            <button class="ml-auto shrink-0 rounded p-0.5 text-text-muted hover:text-primary" (click)="reply.set(null)" aria-label="Clear reply">
              <uw-icon name="x" [size]="13" />
            </button>
          </div>
        }
      </div>

      <div class="hidden h-[calc(100%-100px)] grid-cols-[minmax(280px,0.95fr)_minmax(340px,1.35fr)_minmax(280px,1fr)] gap-3 px-4 pb-4 lg:grid">
        <section class="flex h-full min-h-0 flex-col rounded-xl border border-line bg-surface shadow-sm">
          <div class="min-h-0 flex-1 overflow-y-auto p-3.5">
            <uw-ai-briefing [briefing]="briefing()" [loading]="briefingLoading()" />
          </div>
        </section>

        <section class="flex h-full min-h-0 flex-col rounded-xl border border-line bg-surface shadow-sm">
          <div class="min-h-0 flex-1 overflow-hidden p-3.5">
            <uw-intent-queue />
          </div>
        </section>

        <section class="flex h-full min-h-0 flex-col rounded-xl border border-line bg-surface shadow-sm">
          <div class="min-h-0 flex-1 overflow-y-auto p-3.5">
            <uw-ai-action-hub />
          </div>
        </section>
      </div>

      <div class="flex h-[calc(100%-100px)] min-h-0 flex-col px-4 pb-4 lg:hidden">
        <uw-tabs [tabs]="PANELS" [active]="copilot.mobilePanel()" (activeChange)="setMobilePanel($event)" />
        <div class="mt-3 min-h-0 flex-1 overflow-y-auto rounded-xl border border-line bg-surface p-3.5 shadow-sm">
          @switch (copilot.mobilePanel()) {
            @case ('briefing') {
              <uw-ai-briefing [briefing]="briefing()" [loading]="briefingLoading()" />
            }
            @case ('queue') {
              <uw-intent-queue />
            }
            @case ('actions') {
              <uw-ai-action-hub />
            }
          }
        </div>
      </div>
    </div>
  `,
})
export class UwCopilotTriagePage {
  protected readonly copilot = inject(CopilotStore);
  private readonly copilotService = inject(CopilotService);

  protected readonly PANELS = PANELS;

  protected readonly briefing = signal<AiBriefing | null>(null);
  protected readonly briefingLoading = signal(true);

  protected readonly query = signal('');
  protected readonly reply = signal<string | null>(null);
  protected readonly asking = signal(false);

  constructor() {
    this.copilotService.getBriefing().pipe(take(1)).subscribe((b) => {
      this.briefing.set(b);
      this.briefingLoading.set(false);
    });
  }

  protected ask(): void {
    const q = this.query().trim();
    if (!q || this.asking()) return;
    this.asking.set(true);
    this.copilotService
      .ask(q)
      .pipe(take(1))
      .subscribe((r) => {
        this.reply.set(r);
        this.asking.set(false);
      });
  }

  protected setMobilePanel(panel: string): void {
    this.copilot.setMobilePanel(panel as CopilotPanel);
  }
}
